import { memo, useCallback } from 'react';
import type { ComponentType, MouseEvent as ReactMouseEvent } from 'react';
import { getEdgeClickSelection, getEdgePosition, getMarkerId } from '../../core';
import type { EdgeMarkerType } from '../../core';
import { BezierEdge, SmoothStepEdge, StepEdge } from '../../edges';
import type { EdgeComponentProps } from '../../edges';
import { sameDrawnEdge, selectConnectionMode, selectDrawnEdgeById, selectNodeById, useMindmapStore, useStoreApi } from '../../store';
import type { InternalNode } from '../../store';
import { classNames, isMultiSelectEvent } from '../../utils';

/** Built-in edge types (v11 names); unknown types fall back to the bezier, like v11. */
const EDGE_COMPONENTS: Partial<Record<string, ComponentType<EdgeComponentProps>>> = {
  default: BezierEdge,
  smoothstep: SmoothStepEdge,
  step: StepEdge,
};

const markerUrl = (marker: EdgeMarkerType | undefined): string | undefined => {
  const id = getMarkerId(marker);
  return id ? `url(#${id})` : undefined;
};

const isDrawable = (node: InternalNode | undefined): node is InternalNode =>
  Boolean(node && !node.node.hidden && node.width && node.height);

/** One edge; re-renders only when the edge or one of its two nodes changes. Clicking selects it, then `onEdgeClick`. */
const EdgeWrapperComponent = ({ id }: { id: string }) => {
  const edge = useMindmapStore(selectDrawnEdgeById(id), sameDrawnEdge);
  const source = useMindmapStore(selectNodeById(edge?.source ?? ''));
  const target = useMindmapStore(selectNodeById(edge?.target ?? ''));
  const connectionMode = useMindmapStore(selectConnectionMode);
  const store = useStoreApi();
  const handleClick = useCallback(
    (event: ReactMouseEvent) => {
      const { nodes, edges, applySelection, elementHandlers } = store.getState();
      applySelection(getEdgeClickSelection(nodes, edges, id, isMultiSelectEvent(event)));
      const edge = edges.find((candidate) => candidate.id === id);
      if (edge) elementHandlers.onEdgeClick?.(event.nativeEvent, edge);
    },
    [store, id],
  );
  const handleContextMenu = useCallback(
    (event: ReactMouseEvent) => {
      const { edges, elementHandlers } = store.getState();
      const clicked = edges.find((candidate) => candidate.id === id);
      if (clicked) elementHandlers.onEdgeContextMenu?.(event.nativeEvent, clicked);
    },
    [store, id],
  );

  if (!edge || !isDrawable(source) || !isDrawable(target)) return null;
  const position = getEdgePosition(edge, source, target, connectionMode);
  if (!position) return null;
  const EdgeComponent = EDGE_COMPONENTS[edge.type ?? 'default'] ?? BezierEdge;

  return (
    <g
      className={classNames('cm-edge', edge.selected && 'selected')}
      data-testid={`cm-edge-${id}`}
      aria-label={`Edge from ${edge.source} to ${edge.target}`}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      <EdgeComponent id={id} {...position} markerStart={markerUrl(edge.markerStart)} markerEnd={markerUrl(edge.markerEnd)} />
    </g>
  );
};

export const EdgeWrapper = memo(EdgeWrapperComponent);
